import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Bot, Mail, Check } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Быстрые запросы
const QUICK_REPLIES = [
  { id: 1, text: "Возврат средств", icon: "💰" },
  { id: 2, text: "Цены на услуги", icon: "💇‍♀️" },
  { id: 3, text: "Адреса салонов", icon: "📍" },
  { id: 4, text: "Режим работы", icon: "🕐" },
];

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Инициализация сессии
  useEffect(() => {
    const storedSessionId = localStorage.getItem('chatSessionId');
    const storedEmail = localStorage.getItem('chatEmail');
    
    if (storedSessionId) {
      setSessionId(storedSessionId);
      loadHistory(storedSessionId);
    }
    
    if (storedEmail) {
      setEmail(storedEmail);
      setEmailSubmitted(true);
    }
  }, []);

  // Автоскролл к последнему сообщению
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Фокус на input при открытии
  useEffect(() => {
    if (isOpen && inputRef.current && !showEmailForm) {
      inputRef.current.focus();
    }
  }, [isOpen, showEmailForm]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadHistory = async (sid) => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/history/${sid}`);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Ошибка загрузки истории:', error);
    }
  };

  const createSession = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/chat/session`);
      const newSessionId = response.data.sessionId;
      setSessionId(newSessionId);
      localStorage.setItem('chatSessionId', newSessionId);
      return newSessionId;
    } catch (error) {
      console.error('Ошибка создания сессии:', error);
      return null;
    }
  };

  const handleEmailSubmit = async () => {
    if (!email.trim() || !email.includes('@')) {
      alert('Пожалуйста, введите корректный email');
      return;
    }

    let currentSessionId = sessionId;
    if (!currentSessionId) {
      currentSessionId = await createSession();
      if (!currentSessionId) return;
    }

    try {
      await axios.put(`${API_URL}/api/chat/session/${currentSessionId}`, {
        email: email.trim()
      });
      
      localStorage.setItem('chatEmail', email.trim());
      setEmailSubmitted(true);
      setShowEmailForm(false);
      
      // Показываем приветственное сообщение
      setMessages([{
        role: 'assistant',
        content: `Спасибо! Ваш email ${email.trim()} сохранен. Я готов помочь вам с вопросами о РИО! 😊`,
        createdAt: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Ошибка сохранения email:', error);
      alert('Не удалось сохранить email. Попробуйте еще раз.');
    }
  };

  const handleSendMessage = async (messageText = null) => {
    const textToSend = messageText || inputMessage.trim();
    if (!textToSend) return;

    let currentSessionId = sessionId;
    if (!currentSessionId) {
      currentSessionId = await createSession();
      if (!currentSessionId) return;
    }

    const userMessage = {
      role: 'user',
      content: textToSend,
      createdAt: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      const response = await axios.post(`${API_URL}/api/chat/message`, {
        sessionId: currentSessionId,
        message: textToSend
      });

      setMessages(prev => [...prev, response.data]);
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Извините, произошла ошибка. Попробуйте позже или позвоните нам: 8 (846) 202-88-80',
        createdAt: new Date().toISOString()
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickReply = (text) => {
    handleSendMessage(text);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    if (!isOpen && !emailSubmitted) {
      setShowEmailForm(true);
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Плавающая кнопка */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="fixed bottom-6 right-6 z-50 w-[60px] h-[60px] bg-[#FF6B35] hover:bg-[#E05A2E] text-white rounded-full shadow-[0_10px_25px_rgba(255,107,53,0.3)] hover:shadow-[0_15px_35px_rgba(255,107,53,0.4)] transition-all duration-200 hover:scale-105 flex items-center justify-center"
          aria-label="Открыть чат"
          data-testid="chat-widget-button"
        >
          <MessageCircle size={28} />
        </button>
      )}

      {/* Окно чата */}
      {isOpen && (
        <div
          className="fixed z-[100] bg-white dark:bg-[#1E1E1E] shadow-[0_20px_40px_rgba(0,0,0,0.15)] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 
          max-[768px]:inset-0 max-[768px]:w-full max-[768px]:h-full max-[768px]:rounded-none max-[768px]:bottom-0 max-[768px]:right-0
          min-[769px]:bottom-[100px] min-[769px]:right-6 min-[769px]:w-[380px] min-[769px]:h-[600px] min-[769px]:max-h-[85vh] min-[769px]:rounded-[24px]"
          data-testid="chat-widget-container"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b-[0.5px] border-[#E8E1D8] dark:border-[#3A3A3A] bg-gradient-to-br from-[#F5F1EC] to-[#FEFDFB] dark:from-[#1A1A1A] dark:to-[#252525]">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#FF6B35] flex items-center justify-center">
                <Bot size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-['Playfair_Display'] text-[18px] font-semibold text-[#1A1A1A] dark:text-[#F5F1EC]">
                  Ассистент РИО
                </h3>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-[13px] text-[#1A1A1A]/60 dark:text-[#F5F1EC]/60">Онлайн</span>
                </div>
              </div>
            </div>
            <button
              onClick={toggleChat}
              className="p-2 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 rounded-full transition-colors flex-shrink-0 
              max-[768px]:bg-[#FF6B35]/10 max-[768px]:hover:bg-[#FF6B35]/20"
              aria-label="Закрыть чат"
              data-testid="chat-close-button"
            >
              <X size={24} className="text-[#1A1A1A] dark:text-[#F5F1EC] max-[768px]:text-[#FF6B35] max-[768px]:dark:text-[#FF6B35]" />
            </button>
          </div>

          {/* Email Form */}
          {showEmailForm && !emailSubmitted && (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center max-w-sm">
                <div className="w-16 h-16 rounded-full bg-[#FF6B35]/10 flex items-center justify-center mx-auto mb-4">
                  <Mail size={32} className="text-[#FF6B35]" />
                </div>
                <h4 className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A] dark:text-[#F5F1EC] mb-2">
                  Оставьте ваш email
                </h4>
                <p className="text-sm text-[#1A1A1A]/60 dark:text-[#F5F1EC]/60 mb-6">
                  Мы свяжемся с вами для уточнения деталей вашего запроса
                </p>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleEmailSubmit()}
                  placeholder="example@mail.ru"
                  className="w-full h-12 px-4 bg-[#FAFAF9] dark:bg-[#252525] border border-[#E8E1D8] dark:border-[#3A3A3A] rounded-[24px] text-sm text-[#1A1A1A] dark:text-[#F5F1EC] placeholder:text-[#1A1A1A]/50 dark:placeholder:text-[#F5F1EC]/50 focus:outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 transition-colors mb-4"
                  data-testid="chat-email-input"
                />
                <button
                  onClick={handleEmailSubmit}
                  className="w-full h-12 bg-[#FF6B35] hover:bg-[#E05A2E] text-white rounded-[24px] font-medium transition-colors flex items-center justify-center gap-2"
                  data-testid="chat-email-submit"
                >
                  <Check size={20} />
                  Продолжить
                </button>
                <button
                  onClick={() => {
                    setShowEmailForm(false);
                    setEmailSubmitted(true);
                  }}
                  className="mt-3 text-sm text-[#1A1A1A]/60 dark:text-[#F5F1EC]/60 hover:text-[#FF6B35] transition-colors"
                >
                  Пропустить
                </button>
              </div>
            </div>
          )}

          {/* Messages Area */}
          {!showEmailForm && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages-area">
                {messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full text-center px-6">
                    <Bot size={48} className="text-[#FF6B35] mb-4" />
                    <h4 className="font-['Playfair_Display'] text-lg font-semibold text-[#1A1A1A] dark:text-[#F5F1EC] mb-2">
                      Здравствуйте! 👋
                    </h4>
                    <p className="text-sm text-[#1A1A1A]/60 dark:text-[#F5F1EC]/60 mb-6">
                      Я помогу вам с вопросами о наших услугах, ценах и адресах салонов РИО.
                    </p>
                    
                    {/* Quick Replies */}
                    <div className="grid grid-cols-2 gap-2 w-full max-w-xs max-[400px]:gap-1.5">
                      {QUICK_REPLIES.map(reply => (
                        <button
                          key={reply.id}
                          onClick={() => handleQuickReply(reply.text)}
                          className="px-3 py-2.5 bg-[#F5F1EC] dark:bg-[#2A2A2A] hover:bg-[#FF6B35]/10 dark:hover:bg-[#FF6B35]/20 rounded-[16px] text-xs font-medium text-[#1A1A1A] dark:text-[#F5F1EC] transition-colors text-left border border-[#E8E1D8] dark:border-[#3A3A3A] max-[400px]:px-2 max-[400px]:py-2 max-[400px]:text-[11px] max-[400px]:leading-tight"
                          data-testid={`quick-reply-${reply.id}`}
                        >
                          <span className="mr-1.5 max-[400px]:mr-1">{reply.icon}</span>
                          <span className="break-words">{reply.text}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((message, index) => (
                  <MessageBubble key={index} message={message} />
                ))}

                {isTyping && <TypingIndicator />}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 border-t-[0.5px] border-[#E8E1D8] dark:border-[#3A3A3A]">
                {/* Quick Replies после первого сообщения */}
                {messages.length > 0 && messages.length < 3 && (
                  <div className="flex gap-2 mb-3 overflow-x-auto pb-2 scrollbar-hide">
                    {QUICK_REPLIES.map(reply => (
                      <button
                        key={reply.id}
                        onClick={() => handleQuickReply(reply.text)}
                        className="flex-shrink-0 px-3 py-1.5 bg-[#F5F1EC] dark:bg-[#2A2A2A] hover:bg-[#FF6B35]/10 dark:hover:bg-[#FF6B35]/20 rounded-full text-xs font-medium text-[#1A1A1A] dark:text-[#F5F1EC] transition-colors border border-[#E8E1D8] dark:border-[#3A3A3A] whitespace-nowrap"
                      >
                        <span className="mr-1">{reply.icon}</span>
                        {reply.text}
                      </button>
                    ))}
                  </div>
                )}
                
                <div className="relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Напишите сообщение..."
                    className="w-full h-12 px-4 pr-14 bg-[#FAFAF9] dark:bg-[#252525] border border-[#E8E1D8] dark:border-[#3A3A3A] rounded-[24px] text-sm text-[#1A1A1A] dark:text-[#F5F1EC] placeholder:text-[#1A1A1A]/50 dark:placeholder:text-[#F5F1EC]/50 focus:outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 transition-colors"
                    data-testid="chat-input-field"
                  />
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!inputMessage.trim()}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 bg-[#FF6B35] hover:bg-[#E05A2E] disabled:bg-[#FF6B35]/40 disabled:cursor-not-allowed text-white rounded-full flex items-center justify-center transition-colors"
                    aria-label="Отправить сообщение"
                    data-testid="chat-send-button"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

// MessageBubble Component
const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  const time = new Date(message.createdAt).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
      data-testid={`chat-message-${message.role}`}
    >
      <div
        className={`max-w-[85%] px-4 py-3 rounded-[18px] ${isUser
          ? 'bg-[#FF6B35] text-white rounded-br-[4px]'
          : 'bg-[#F5F1EC] dark:bg-[#2A2A2A] text-[#1A1A1A] dark:text-[#F5F1EC] rounded-bl-[4px]'
        }`}
      >
        <p className="text-[14px] leading-[1.5] whitespace-pre-wrap break-words">
          {message.content}
        </p>
        <span
          className={`text-[11px] mt-1 block opacity-60 ${isUser ? 'text-right' : 'text-left'
          }`}
        >
          {time}
        </span>
      </div>
    </div>
  );
};

// TypingIndicator Component
const TypingIndicator = () => {
  return (
    <div className="flex justify-start" data-testid="chat-typing-indicator">
      <div className="max-w-[70%] px-4 py-3 rounded-[18px] rounded-bl-[4px] bg-[#F5F1EC] dark:bg-[#2A2A2A]">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 bg-[#FF6B35] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-[#FF6B35] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-[#FF6B35] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>
  );
};

export default ChatWidget;
